import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineAddCard } from "react-icons/md";
import debounce from "lodash/debounce";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import Paggination from "../Paggination";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";

const ScriptListLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncPatchApiCall, AsyncDeleteApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ data: [], total: 0, totalPages: 0, currentPage: 1 });
  const [segments, setSegments] = useState([]);
  const [discoveryStatus, setDiscoveryStatus] = useState({ isActive: false, remainingMs: 0 });
  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 50,
    search: "",
    segment: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    exchange: "",
    segment: "",
    isActive: true,
  });

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const result = await AsyncGetApiCall("/trading-settings/scripts", query);
      if (result && result.success) {
        setData({
          data: Array.isArray(result.data) ? result.data : [],
          total: result.total ?? 0,
          totalPages: Math.max(1, result.totalPages ?? 1),
          currentPage: result.currentPage ?? query.pageNo,
        });
      }
    } catch (error) {
      console.error("Error fetching scripts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSegments = async () => {
    try {
      const result = await AsyncGetApiCall("/trading-settings/segments/list?status=active&inSelectMode=true");
      if(result?.success){
        setSegments(result?.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchDiscoveryStatus = async () => {
    try {
      const result = await AsyncGetApiCall("/market-data/discovery-status");
      if (result && result.success) {
        setDiscoveryStatus({ isActive: result.isActive, remainingMs: result.remainingMs });
      }
    } catch (e) {}
  };

  const handleStartDiscovery = async () => {
    try {
      const result = await AsyncPostApiCall("/market-data/start-discovery", {});
      if (result && result.success) {
        toast.success(result.message);
        fetchDiscoveryStatus();
      }
    } catch (e) {
      toast.error("Failed to start discovery");
    }
  };

  useEffect(() => {
    fetchScripts();
  }, [query]);

  useEffect(() => {
    fetchSegments();
    fetchDiscoveryStatus();
    const interval = setInterval(fetchDiscoveryStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
    [],
  );

  const handleSearch = (e) => debouncedSearch(e.target.value);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditData(item);
      setFormData({
        name: item.name || "",
        exchange: item.exchange || "",
        segment: item.segment || "",
        isActive: item.isActive !== false,
      });
    } else {
      setEditData(null);
      setFormData({
        name: "",
        exchange: "",
        segment: "",
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      // Use uppercase NAME:EXCHANGE as scriptId for uniqueness
      const payload = { 
        ...formData, 
        scriptId: `${formData.name}:${formData.exchange}`.toUpperCase() 
      };
      
      if (editData) {
        result = await AsyncPatchApiCall(`/trading-settings/scripts/update/${editData._id}`, payload);
      } else {
        result = await AsyncPostApiCall("/trading-settings/scripts/create", payload);
      }
      
      if (result && result.success !== false && !result.errors) {
        toast.success(editData ? "Instrument updated" : "Instrument created");
        handleCloseModal();
        setQuery((prev) => ({ ...prev, pageNo: 1 }));
        fetchScripts();
      } else {
        const errorMsg = result?.message || (result?.errors ? "Validation error" : "Operation failed");
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    setLoading(true);
    try {
      const result = await AsyncDeleteApiCall(`/trading-settings/scripts/delete/${id}`);
      if (result) {
        toast.success("Instrument deleted");
        setQuery((prev) => ({ ...prev, pageNo: 1 }));
        fetchScripts();
      }
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <div style={{ minWidth: "92px" }}>
              <SearchableSelect
                placeholder="Show"
                value={String(query.limitVal)}
                showSearch={false}
                onChange={(e) =>
                  setQuery((prev) => ({
                    ...prev,
                    limitVal: parseInt(e.target.value, 10),
                    pageNo: 1,
                  }))
                }
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
            <div style={{ minWidth: "180px" }}>
              <SearchableSelect
                placeholder="All Segments"
                value={query.segment}
                onChange={(e) =>
                  setQuery((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    pageNo: 1,
                  }))
                }
                options={
                  Array.isArray(segments)
                    ? segments.map((s) => ({
                        label: s.displayName || s.name,
                        value: s.name,
                      }))
                    : []
                }
              />
            </div>
            <div className="navbar-search d-flex align-items-center">
              <input
                type="text"
                className="bg-base h-40-px border radius-8 px-12"
                style={{ width: "250px" }}
                placeholder="Search instrument name..."
                onChange={handleSearch}
              />
              <Icon icon="ion:search-outline" className="icon ms-[-30px]" />
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className={`btn ${discoveryStatus.isActive ? "btn-success" : "btn-warning"} radius-8 px-17 py-9 d-flex align-items-center gap-2 text-white fw-semibold`}
              onClick={handleStartDiscovery}
              disabled={discoveryStatus.isActive}
            >
              <Icon icon={discoveryStatus.isActive ? "eos-icons:loading" : "mdi:radar"} className="text-xl" />
              {discoveryStatus.isActive 
                ? `Discovering... (${Math.ceil(discoveryStatus.remainingMs/60000)}m left)` 
                : "Discover Symbols (5 Min)"}
            </button>
            <button
              type="button"
              className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2"
              onClick={() => handleOpenModal()}
            >
              <MdOutlineAddCard className="text-xl" />
              Add Instrument
            </button>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">Instrument Name</th>
                  <th scope="col">Exchange</th>
                  <th scope="col">Segment</th>
                  <th scope="col" className="text-center">Status</th>
                  <th scope="col" className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.data.length > 0 ? (
                  data.data.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <span className="fw-semibold text-primary-light text-uppercase">{item.name}</span>
                      </td>
                      <td>{item.exchange}</td>
                      <td>{item.segment}</td>
                      <td className="text-center">
                        <span className={`badge ${item.isActive ? "bg-success-focus text-success-600" : "bg-danger-focus text-danger-600"}`}>
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                          <button
                            className="bg-success-focus text-success-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleOpenModal(item)}
                          >
                            <Icon icon="lucide:edit" />
                          </button>
                          <button
                            className="bg-danger-focus text-danger-600 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleDelete(item._id)}
                          >
                            <Icon icon="mdi:delete-outline" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-secondary-light py-20">No instruments found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paggination
            total={data.total}
            totalPages={data.totalPages}
            currentPage={data.currentPage}
            setPageNo={(page) =>
              setQuery((prev) => ({ ...prev, pageNo: page }))
            }
            startFrom={
              data.total > 0 ? (data.currentPage - 1) * query.limitVal + 1 : 0
            }
            endTo={
              data.total > 0
                ? (data.currentPage - 1) * query.limitVal + data.data.length
                : 0
            }
          />
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 radius-16 shadow-lg">
              <div className="modal-header bg-primary-600 py-16 px-24 border-0">
                <h5 className="modal-title text-white">{editData ? "Edit Instrument" : "Add Instrument"}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleCloseModal}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-24">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="form-label text-secondary-light fw-bold text-xs uppercase">INSTRUMENT NAME</label>
                      <input
                        type="text"
                        className="form-control radius-8 text-uppercase"
                        placeholder="e.g. NIFTY, BANKNIFTY, BTCUSDT"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-secondary-light fw-bold text-xs">EXCHANGE</label>
                      <SearchableSelect
                        placeholder="Select Exchange"
                        value={formData.exchange}
                        onChange={(e) => {
                          const ex = e.target.value;
                          let seg = ex;
                          if (ex === "NOPT") seg = "NFO-OPT";
                          else if (ex === "NFUT") seg = "NFO-FUT";
                          else if (ex === "MCX") seg = "MCX-FUT";
                          else if (ex === "NSE") seg = "NSE";
                          else if (ex === "FOREX") seg = "FOREX";
                          else if (ex === "COMEX") seg = "COMEX";
                          setFormData({ ...formData, exchange: ex, segment: seg });
                        }}
                        options={[
                          { label: "NSE", value: "NSE" },
                          { label: "NOPT (NFO Options)", value: "NOPT" },
                          { label: "NFUT (NFO Futures)", value: "NFUT" },
                          { label: "MCX", value: "MCX" },
                          { label: "FOREX", value: "FOREX" },
                          { label: "COMEX", value: "COMEX" },
                        ]}
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label text-secondary-light fw-bold text-xs">SEGMENT (AUTO-DETECTED)</label>
                      <input
                        type="text"
                        className="form-control radius-8 bg-light text-uppercase fw-semibold"
                        value={formData.segment}
                        readOnly
                        placeholder="Inferred from Exchange"
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          id="instrumentActive"
                        />
                        <label className="form-check-label text-secondary-light fw-bold text-xs" htmlFor="instrumentActive">
                          ACTIVE FOR TRADING
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0 p-24">
                  <button type="button" className="btn btn-secondary radius-8 px-24" onClick={handleCloseModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary-600 radius-8 px-24">{editData ? "Update" : "Save"} Instrument</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScriptListLayer;
